export type LogLevel = "trace" | "debug" | "info" | "warn" | "error" | "silent";

type LoggerMethod = (...args: any[]) => void;

type Logger = {
  level: LogLevel;
  trace: LoggerMethod;
  debug: LoggerMethod;
  info: LoggerMethod;
  warn: LoggerMethod;
  error: LoggerMethod;
};

const LEVEL_RANK: Record<LogLevel, number> = {
  trace: 10,
  debug: 20,
  info: 30,
  warn: 40,
  error: 50,
  silent: 999,
};

const normalizeLevel = (value: unknown): LogLevel | null => {
  const raw = String(value ?? "")
    .trim()
    .toLowerCase();
  if (
    raw === "trace" ||
    raw === "debug" ||
    raw === "info" ||
    raw === "warn" ||
    raw === "warning" ||
    raw === "error" ||
    raw === "silent" ||
    raw === "none"
  ) {
    if (raw === "warning") return "warn";
    if (raw === "none") return "silent";
    return raw as LogLevel;
  }
  return null;
};

const resolveDefaultLevel = (): LogLevel => {
  const explicit = normalizeLevel(process.env.REACT_APP_LOG_LEVEL);
  if (explicit) return explicit;

  const debugLogs = String(process.env.REACT_APP_DEBUG_LOGS || "")
    .trim()
    .toLowerCase();
  if (debugLogs === "true" || debugLogs === "1" || debugLogs === "yes") {
    return "trace";
  }

  return process.env.NODE_ENV === "development" ? "debug" : "warn";
};

export const createLogger = (
  namespace: string,
  options?: { level?: LogLevel },
): Logger => {
  const level = options?.level ?? resolveDefaultLevel();
  const minRank = LEVEL_RANK[level];
  const prefix = namespace ? `[${namespace}]` : "";

  const write = (
    msgLevel: Exclude<LogLevel, "silent">,
    consoleFn: LoggerMethod,
    args: any[],
  ) => {
    if (LEVEL_RANK[msgLevel] < minRank) return;
    if (!prefix) {
      consoleFn(...args);
      return;
    }
    consoleFn(prefix, ...args);
  };

  return {
    level,
    trace: (...args) => write("trace", console.debug, args),
    debug: (...args) => write("debug", console.debug, args),
    info: (...args) => write("info", console.info, args),
    warn: (...args) => write("warn", console.warn, args),
    error: (...args) => write("error", console.error, args),
  };
};
